import { type ZodType, z } from 'zod';
import type { Platform } from '../context/env-context';

export class ApiResponseError<TData = unknown> extends Error {
  public readonly status: number;
  public readonly data: TData;

  public static errorSchemas = {
    errors: z.object({ errors: z.array(z.object({ code: z.string().optional() })) }),
    detail: z.object({ detail: z.string().optional() }),
    array: z.array(
      z.object({
        line: z.number().optional(),
        field: z.string().optional(),
        message: z.string(),
      }),
    ),
    message: z.object({ message: z.string().optional() }),
  };

  constructor(status: number, data: TData, message?: string) {
    super(message ?? `Request failed with status code ${status}`);
    this.name = 'ApiResponseError';
    this.status = status;
    this.data = data;
    Object.setPrototypeOf(this, ApiResponseError.prototype);
  }

  static isApiResponseError<TData = unknown>(
    err: unknown,
    expectedDataShape: ZodType<TData>,
  ): err is ApiResponseError<TData> {
    if (!(err instanceof ApiResponseError)) {
      return false;
    }

    return expectedDataShape.safeParse(err.data).success;
  }
}

export class NavigationError extends Error {
  constructor(message?: string) {
    super(message ?? 'Navigation error');
    this.name = 'NavigationError';
    Object.setPrototypeOf(this, NavigationError.prototype);
  }
}

export class NavigationPathIdNotResolvedError extends NavigationError {
  constructor(
    public readonly pathId: string,
    public readonly platform?: Platform,
  ) {
    super(`Navigation error, path not found`);
    this.name = 'NavigationPathIdNotResolvedError';
    Object.setPrototypeOf(this, NavigationPathIdNotResolvedError.prototype);
  }
}
